import { create } from 'zustand';
import { clearFormDraftsByPrefix, DEV_PORTAL_DRAFT_PREFIX } from '@/lib/formDraftStorage';
import { authApiAdapter } from '@/services/api';
import type { User } from '@/types/index';

export interface AuthApiClient {
  logout: () => Promise<any>;
  getCurrentUser: () => Promise<any>;
  validateToken: () => Promise<any>;
  sendOtp: (email: string) => Promise<any>;
  verifyOtp: (email: string, token: string, name?: string) => Promise<any>;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitializing: boolean;
  apiClient: AuthApiClient | null;
  setAuth: (user: User) => void;
  logout: () => Promise<void>;
  setInitializing: (isInitializing: boolean) => void;
  setApiClient: (client: AuthApiClient) => void;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isInitializing: true,
  apiClient: authApiAdapter,

  setAuth: (user: User) => {
    set({ user, isAuthenticated: true, isLoading: false, isInitializing: false });
  },

  logout: async () => {
    try {
      const { apiClient } = get();
      if (apiClient) {
        await apiClient.logout();
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    }
    clearFormDraftsByPrefix(DEV_PORTAL_DRAFT_PREFIX);
    set({ user: null, isAuthenticated: false, isLoading: false, isInitializing: false });
  },

  setInitializing: (isInitializing: boolean) => {
    set({ isInitializing });
  },

  setApiClient: (client: AuthApiClient) => {
    set({ apiClient: client });
  },

  checkAuth: async () => {
    const { apiClient, isLoading, isInitializing } = get();
    if (isLoading) {
      return;
    }

    if (!apiClient) {
      set({ isLoading: false, isInitializing: false });
      return;
    }

    // Full-page loader only on first boot; background rechecks stay quiet.
    if (isInitializing) {
      set({ isLoading: true });
    }

    try {
      const response = await apiClient.getCurrentUser();
      if (response.success && response.data) {
        // Verify user has developer role
        if (response.data.role !== 'developer') {
          console.error('User is not a developer');
          await apiClient.logout();
          set({ user: null, isAuthenticated: false, isLoading: false, isInitializing: false });
          return;
        }
        set({ user: response.data, isAuthenticated: true, isLoading: false, isInitializing: false });
      } else {
        set({ user: null, isAuthenticated: false, isLoading: false, isInitializing: false });
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      set({ user: null, isAuthenticated: false, isLoading: false, isInitializing: false });
    }
  },
}));

